import { showDialog } from '@adornis/dialog/dialog-functions.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { DialogHeader } from '@adornis/components/client/DialogHeader.js';
import { html } from 'lit';

const PersonImportSyntaxInformationDialog = stateful(
  ({ resolve, reject }: { resolve: () => void; reject: () => void }) => {
    const personCsvExample = `debitorennummer;email;stammdaten_geschlecht;stammdaten_geburtsDatum;stammdaten_nachname;stammdaten_vorname;
2000042;max.mustermann@example.com;M;14.04.2015;Mustermann;Max`;

    const sorgeberechtigteCsvExample = `debitorennummer;email;stammdaten_nachname;stammdaten_vorname;debitorennummer_sorgeberechtigte_0;email_sorgeberechtigte_0
2000042;max.mustermann@example.com;Mustermann;Max;2000043;max.eltern@example.com`;

    const classBookingExample = `debitorennummer;schule_kostenstelle;klasse;jahrgangsstufe;startdatum;enddatum
2000042;123;a;7;01.08.2025;31.07.2026`;

    const oldDebitorennummerExample = `debitorennummer;schule_kostenstelle;alte_debitorennummer
2000042;123;465`;

    return html`
      <div class="dialog-container">
        ${DialogHeader({
          title: 'Wie funktioniert der Personen-Import?',
          close: reject,
          closeLabel: 'Dialog schließen',
        })}

        <p>
          Für jede CSV Zeile wird eine Person erstellt oder aktualisiert. Gesucht wird zuerst über die Externe ID
          ("externalUniqueID"), dann über die "debitorennummer". Danach versucht der Import, anhand von anderen
          Merkmalen eine existierende Person zu finden und diese zu aktualisieren. Wenn keine existierende Person
          gefunden wird, wird eine neue Person erstellt. Bevor der Import ausgeführt wird, wird eine Vorschau mit allen
          Änderungen angezeigt. Hier können die gefunden Personen vom System nochmal überprüft und bei Bedarf angepasst
          werden. Alle Felder sind optional. Die folgenden Felder werden direkt auf der Person gesetzt:
        </p>
        <div class="border-base-300 bg-base-200 px-6 py-4 rounded-box">
          <p class="text-sm font-semibold text-base-content/70">Verfügbare direkte Felder:</p>
          <ul>
            <li>"externalUniqueID" (Externe ID, Hauptschlüssel)</li>
            <li>"debitorennummer" (Schlüssel)</li>
            <li>"email"</li>
            <li>"stammdaten_geschlecht" (M, W, D)</li>
            <li>"stammdaten_geburtsDatum" (DD.MM.YYYY)</li>
            <li>"stammdaten_vorsatzWort"</li>
            <li>"stammdaten_namenszusatz"</li>
            <li>"stammdaten_nachname"</li>
            <li>"stammdaten_vorname"</li>
            <li>"stammdaten_titel"</li>
            <li>"stammdaten_telefonNr"</li>
            <li>"adresse_streetAndNumber"</li>
            <li>"adresse_zip"</li>
            <li>"adresse_city"</li>
            <li>"adresse_country"</li>
            <li>"adresse_auslandsPlz"</li>
            <li>"bankInfos_iban"</li>
            <li>"bankInfos_kontoinhaber"</li>
          </ul>
        </div>

        <div class="border-base-300 bg-base-200 px-6 py-4 rounded-box gap-1">
          <p class="text-sm font-semibold text-base-content/70">Beispiel:</p>
          <pre class="bg-base-100 rounded-box overflow-x-auto p-4 text-sm"><code>${personCsvExample}</code></pre>
          <p>
            Importiert eine Person mit der Debitorennummer 2000042. Wenn es bereits eine Person mit dieser
            Debitorennummer gibt, wird diese aktualisiert.
          </p>
        </div>

        <h4 class="border-b border-base-300 pb-1 font-semibold">Wie findet der Import eine bestehende Person?</h4>
        <p>
          Für jede Zeile wird in drei Stufen nach einer bestehenden Person gesucht. Die erste Stufe, die etwas findet,
          gewinnt:
        </p>
        <div class="border-base-300 bg-base-200 px-6 py-4 rounded-box">
          <ol class="list-decimal list-inside flex flex-col gap-1">
            <li><b>Debitorennummer</b> - eindeutig, findet die Person immer.</li>
            <li>
              <b>E-Mail</b> - findet die Person, wenn die Adresse genau so gespeichert ist. Teilen sich mehrere Personen
              eine Adresse, entscheidet zusätzlich der Name.
            </li>
            <li>
              <b>Name + Schule + Geburtsdatum</b> - nur wenn "schule_kostenstelle" gefüllt ist. Verglichen wird der
              komplette Name gegen alle Personen mit einer Rolle an dieser Schule, bei Sorgeberechtigten zusätzlich
              gegen die bereits mit dem Kind verknüpften Personen.
            </li>
          </ol>
        </div>
        <p>Das Geburtsdatum entscheidet in der dritten Stufe mit, sobald es auf beiden Seiten bekannt ist:</p>
        <div class="border-base-300 bg-base-200 px-6 py-4 rounded-box">
          <ul class="flex flex-col gap-1">
            <li>
              <b>Verschiedene Geburtstage</b> schließen eine Person aus - ein "Max" ist kein "Max Gustav", nur weil
              beide Max heißen.
            </li>
            <li>
              <b>Gleicher Geburtstag</b> lässt schon eine grobe Namensähnlichkeit als Treffer zu, etwa "Max" zu "Max
              Gustav" - gleiche Schule, gleicher Geburtstag und ein ähnlicher Name ist dieselbe Person.
            </li>
          </ul>
          <p class="pt-2">
            Ein Geburtsdatum in der Datei ("stammdaten_geburtsDatum") macht die Suche also deutlich treffsicherer.
          </p>
        </div>
        <h4 class="border-b border-base-300 pb-1 font-semibold">Die Spalte "Sicherheit"</h4>
        <p>
          Die Prozentzahl sagt <b>nicht</b>, wie ähnlich zwei Namen sind, sondern wie sicher sich der Import bei seiner
          Entscheidung ist - und zwar in beide Richtungen. "An dieser Schule heißt niemand auch nur annähernd so" ist
          eine genauso sichere Entscheidung wie ein Treffer über die Debitorennummer.
        </p>
        <div class="border-base-300 bg-base-200 px-6 py-4 rounded-box">
          <ul class="flex flex-col gap-2">
            <li>
              <b>Hoch (ab 90%)</b> - der Import ist sich sicher, egal ob er aktualisiert oder neu anlegt. Die Zeile wird
              automatisch genehmigt.
            </li>
            <li>
              <b>Niedrig</b> - der Name passt zu gut zum Ignorieren und zu schlecht zum Zuordnen. Nur diese Zeilen
              müssen von Hand entschieden werden; sie stehen als Fehler ganz oben in der Tabelle.
            </li>
          </ul>
          <p class="pt-2">
            Genau ein Fall bleibt also übrig: gleiche Schule, fast gleicher Name, aber eben nur fast. Alles andere
            entscheidet der Import selbst.
          </p>
        </div>

        <p>Findet keine Stufe etwas, wird eine neue Person angelegt. Zwei Dinge sind dabei wichtig:</p>
        <div class="border-base-300 bg-base-200 px-6 py-4 rounded-box">
          <ul class="flex flex-col gap-1">
            <li>
              Ohne Geburtsdatum verzeiht die Namenssuche Tippfehler, aber keine
              <b>zusätzlichen Namensbestandteile</b>. Steht in der Datei "Max" und in der Datenbank "Max Gustav", reicht
              die Ähnlichkeit allein nicht - die Zeile legt neu an und meldet den Kandidaten mit beiden Geburtsdaten zur
              Prüfung. Mit gleichem Geburtsdatum wird daraus ein Treffer.
            </li>
            <li>
              <b>Vertauschte Spalten</b> sind dagegen kein Problem: Stehen Vor- und Nachname in der Datei andersherum,
              wird die Person trotzdem gefunden und die Vorschau weist als Hinweis darauf hin.
            </li>
            <li>
              Sorgeberechtigte werden an der Schule ihres Kindes gesucht - die Schule der Zeile gilt also auch für sie,
              und zusätzlich werden immer die bereits mit diesem Kind verknüpften Personen geprüft. Eine Klassenbuchung
              entsteht dadurch nicht.
            </li>
            <li>
              Trägt eine Sorgeberechtigten-Spalte die <b>Debitorennummer oder E-Mail des Kindes</b>, wird sie ignoriert
              - niemand ist sein eigener Sorgeberechtigter. Sonst würde die Zeile den Namen des Kindes überschreiben.
              Die Vorschau meldet das als Fehler.
            </li>
          </ul>
        </div>

        <h4 class="border-b border-base-300 pb-1 font-semibold">Hinweise in der Vorschau</h4>
        <p>
          Die Vorschau bewertet jede Zeile, bevor irgendetwas geschrieben wird. Zeilen mit Hinweisen stehen oben in der
          Tabelle.
        </p>
        <div class="border-base-300 bg-base-200 px-6 py-4 rounded-box">
          <ul class="flex flex-col gap-2">
            <li>
              <span class="badge badge-error badge-sm border-none">Fehler</span>
              Die Zeile wird <b>nicht automatisch genehmigt</b>. Sie legt vermutlich eine Person doppelt an oder
              verweist auf eine Kostenstelle, die es nicht gibt. Bitte die Person zuordnen oder die Zeile bewusst
              genehmigen.
            </li>
            <li>
              <span class="badge badge-warning badge-sm border-none">Warnung</span>
              Die Zeile wird ausgeführt, sollte aber geprüft werden - zum Beispiel eine Person, die nur über die
              Namensähnlichkeit gefunden wurde, ein Name, der stark vom gespeicherten abweicht und deshalb zu einer
              anderen Person gehören könnte, oder eine Zeile, die Werte löscht.
            </li>
            <li>
              <span class="badge badge-ghost badge-sm">Hinweis</span>
              Nur zur Information - etwa dass Vor- und Nachname vertauscht sind, oder dass die Zeile eine neue Person
              anlegt, weil sie keine Debitorennummer, E-Mail oder Schule enthält und deshalb auch beim nächsten Import
              niemanden finden kann. Eine neue Person anzulegen ist ein normales Ergebnis.
            </li>
          </ul>
        </div>

        <h4 class="border-b border-base-300 pb-1 font-semibold">Import von Sorgeberechtigten</h4>
        <p>
          Sorgeberechtigte können direkt mit ihren Kindern importiert werden. Dazu können in einer Zeile, die in diesem
          Fall als "Kind" zählt, beliebig viele weitere Felder wie oben beschrieben hinzugefügt werden. Mit dem Zusatz,
          dass sie mit dem unten genannten Suffix ergänzt werden müssen. Das "N" muss hierbei durch eine fortlaufende
          Zahl ersetzt werden, um mehrere Sorgeberechtigte und deren Felder zu unterscheiden
        </p>

        <div class="border-base-300 bg-base-200 px-6 py-4 rounded-box">
          <p class="text-sm font-semibold text-base-content/70">SbS Suffix:</p>

          <p>"_sorgeberechtigte_N" (N = 0, 1, 2, 3, ...)</p>
        </div>

        <div class="border-base-300 bg-base-200 px-6 py-4 rounded-box gap-1">
          <p class="text-sm font-semibold text-base-content/70">Beispiel:</p>
          <pre
            class="bg-base-100 rounded-box overflow-x-auto p-4 text-sm"
          ><code>${sorgeberechtigteCsvExample}</code></pre>
          <p>
            Importiert eine Person mit der Debitorennummer 2000042. Gleichzeitig wird eine zweite Person mit der
            Debitorennummer 2000043 importiert. Diese wird auch erst im System gesucht und wenn möglich aktualisiert. Da
            sie als Sorgeberechtigte importiert wird, wird sie automatisch mit der Person der Zeile (dem Kind mit der
            Debitorennummer 2000042) verknüpft.
          </p>
        </div>

        <h4 class="border-b border-base-300 pb-1 font-semibold">Klassenbuchungen</h4>
        <p>
          Der Import kann zu einer beliebigen Person (auch SbS innerhalb einer Zeile) Klassenbuchungen anlegen und
          aktualisieren. Dafür müssen die Schule und entweder das Schuljahr oder mindestens eines der beiden Daten
          ausgefüllt werden.
        </p>
        <div class="border-base-300 bg-base-200 px-6 py-4 rounded-box">
          <p class="text-sm font-semibold text-base-content/70">Klassenbuchungsfelder:</p>
          <ul>
            <li>"schule_kostenstelle" (Der Kostenstellen-Präfix der Schule)</li>
            <li>"klasse"</li>
            <li>"jahrgangsstufe"</li>
            <li>"schuljahr" (z.B. "25 / 26" oder "25/26")</li>
            <li>"startdatum" (TT.MM.JJJJ, in der Regel der 01.08.)</li>
            <li>"enddatum" (TT.MM.JJJJ, in der Regel der 31.07.)</li>
          </ul>
          <p class="pt-2">
            Die Laufzeit ergibt sich aus dem Schuljahr: Fehlt das Start- oder Enddatum, wird der 01.08. beziehungsweise
            der 31.07. dieses Schuljahres eingesetzt. Ein Datum aus der Datei gewinnt immer - so kann zum Beispiel ein
            Schüler mit einem abweichenden Startdatum mitten im Schuljahr aufgenommen werden. Die eingesetzte Laufzeit
            steht in der Vorschau, bevor sie geschrieben wird.
          </p>
          <p class="pt-2">
            Wird kein Schuljahr angegeben, wird es aus den Daten abgeleitet - Start- und Enddatum müssen dann im selben
            Schuljahr liegen. Felder, die in der CSV fehlen, bleiben auf der Buchung unverändert.
          </p>
        </div>

        <div class="border-base-300 bg-base-200 px-6 py-4 rounded-box gap-1">
          <p class="text-sm font-semibold text-base-content/70">Beispiel:</p>
          <pre class="bg-base-100 rounded-box overflow-x-auto p-4 text-sm"><code>${classBookingExample}</code></pre>
          <p>
            Importiert eine Person mit der Debitorennummer 2000042. Eine Person hat pro Schule und Schuljahr höchstens
            eine Klassenbuchung - der Import sucht also die Buchung an der Schule mit dem Kostenstellen-Präfix "123" im
            Schuljahr "25/26". Wird sie gefunden, werden Jahrgangsstufe, Klasse und Laufzeit aktualisiert, sofern sie
            sich unterscheiden. Jede Abweichung erscheint einzeln in der Vorschau. Wird keine Buchung gefunden, wird
            eine neue angelegt.
          </p>
        </div>

        <h4 class="border-b border-base-300 pb-1 font-semibold">Alte Debitorennummern</h4>
        <p>
          Der Import kann zu Teilen auch die alten Debitorennummern verwalten. Dafür muss das Feld
          "alte_debitorennummer" ausgefüllt werden. Da eine alte Debitorennummer auch zu einer Schule gehören kann, wird
          das Feld "schule_kostenstelle" hier zusätzlich berücksichtigt, wenn es angegeben ist. Sollte dieses leer
          gelassen werden, wird die alte Debitorennummer ohne Schule eingetragen.
        </p>
        <p class="font-semibold text-error">
          Achtung: Der Import aktualisert hierbei nur den ersten Eintrag der alten Debitorennummern auf einer Person!
        </p>
        <div class="border-base-300 bg-base-200 px-6 py-4 rounded-box">
          <p class="text-sm font-semibold text-base-content/70">Alte Debitorennummer Felder:</p>
          <ul>
            <li>"alte_debitorennummer" (Eine beliebige Zeichenkette als alte Debitorennummer)</li>
          </ul>
        </div>

        <div class="border-base-300 bg-base-200 px-6 py-4 rounded-box gap-1">
          <p class="text-sm font-semibold text-base-content/70">Beispiel:</p>
          <pre
            class="bg-base-100 rounded-box overflow-x-auto p-4 text-sm"
          ><code>${oldDebitorennummerExample}</code></pre>
          <p>
            Importiert eine Person mit der Debitorennummer 2000042. Wenn die Person im System gefunden wird, wird
            überprüft ob es bereits eine alte Debitorennummer "465" für die Schule mit dem Kostenstellen-Präfix "123"
            gibt. Wenn nein, wird eine neue alte Debitorennummer angelegt. Hierbei wird exakt auf beide Seiten geachtet.
            Sollte sich zum Beispiel nur die Nummer verändern, wird der Eintrag trotzdem aktualisert.
          </p>
        </div>

        <h4 class="border-b border-base-300 pb-1 font-semibold">Das Löschen von Werten</h4>
        <p>
          Der Import kann bestimmte Werte löschen. Dafür muss das Feld explizit mit dem Wert "DELETE" gefüllt werden, da
          leere Felder im Import mit Absicht ignoriert werden. Das "DELETE" Schlüsselwort ist für die folgenden Felder
          verfügbar:
        </p>
        <div class="border-base-300 bg-base-200 px-6 py-4 rounded-box">
          <p class="text-sm font-semibold text-base-content/70">Verfügbare direkte Felder:</p>
          <ul>
            <li>"debitorennummer" (Hauptschlüssel)</li>
            <li>"email"</li>
            <li>"stammdaten_geschlecht" (M, W, D)</li>
            <li>"stammdaten_geburtsDatum" (DD.MM.YYYY)</li>
            <li>"stammdaten_vorsatzWort"</li>
            <li>"stammdaten_namenszusatz"</li>
            <li>"stammdaten_nachname"</li>
            <li>"stammdaten_vorname"</li>
            <li>"stammdaten_titel"</li>
            <li>"stammdaten_telefonNr"</li>
            <li>"adresse_streetAndNumber"</li>
            <li>"adresse_zip"</li>
            <li>"adresse_city"</li>
            <li>"adresse_country"</li>
            <li>"adresse_auslandsPlz"</li>
            <li>"bankInfos_iban"</li>
            <li>"bankInfos_kontoinhaber"</li>
            <li>"alte_debitorennummer" (Hier wird nur der erste Eintrag gelöscht)</li>
          </ul>
        </div>
      </div>
    `;
  },
);

export async function openPersonImportSyntaxInformationDialog(): Promise<void> {
  return showDialog({
    content: (resolve, reject) => PersonImportSyntaxInformationDialog({ resolve, reject }),
    modalBoxClasses: 'w-[60vw] h-[60vh] max-w-[60vw] max-h-[60vh]',
  });
}
